import { Controller } from '@hotwired/stimulus';

// Ships with SidebarComponent — owns all of its interactive behavior
// (collapse/expand, persistence, mobile drawer, backdrop, resize).
// Zero dependency on any host-app controller: drop the component into any
// Rails app with Stimulus installed and it works out of the box.
//
// Trigger it from anywhere on the page, including outside the component's
// own DOM subtree, by dispatching: window.dispatchEvent(new CustomEvent('sidebar-component:toggle'))
// (see event_dispatcher_controller.js for a declarative way to do that from a button).
export default class extends Controller {
  static targets = ['panel', 'backdrop'];

  static values = {
    expandedWidth: { type: String, default: '18rem' },
    collapsedWidth: { type: String, default: '5rem' },
    breakpoint: { type: Number, default: 1024 },
    storageKey: { type: String, default: 'sidebar-component:collapsed' },
    cssVariable: { type: String, default: '--sidebar-component-width' },
  };

  connect() {
    this.collapsed = localStorage.getItem(this.storageKeyValue) === 'true';
    this.mobileOpen = false;
    this._resizeHandler = () => this.applyState(false);
    window.addEventListener('resize', this._resizeHandler);
    this.applyState(false);
  }

  disconnect() {
    window.removeEventListener('resize', this._resizeHandler);
  }

  toggle() {
    if (this.isDesktop) {
      this.collapsed = !this.collapsed;
      localStorage.setItem(this.storageKeyValue, String(this.collapsed));
      this.applyState(true);
    } else if (this.mobileOpen) {
      this.closeMobile();
    } else {
      this.openMobile();
    }
  }

  closeMobile() {
    this.mobileOpen = false;
    this.panelTarget.classList.add('hidden');
    this.backdropTarget.classList.add('hidden');
  }

  openMobile() {
    this.mobileOpen = true;
    this.panelTarget.classList.remove('hidden');
    this.backdropTarget.classList.remove('hidden');
  }

  get isDesktop() {
    return window.innerWidth >= this.breakpointValue;
  }

  applyState(animate) {
    const collapsed = this.isDesktop && this.collapsed;
    this.panelTarget.dataset.collapsed = collapsed ? 'true' : 'false';

    const width = collapsed ? this.collapsedWidthValue : this.expandedWidthValue;

    if (animate) {
      this.panelTarget.style.transition = 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
    } else {
      this.panelTarget.style.transition = 'none';
      requestAnimationFrame(() => { this.panelTarget.style.transition = ''; });
    }
    this.panelTarget.style.width = this.isDesktop ? width : '';

    document.documentElement.style.setProperty(this.cssVariableValue, this.isDesktop ? width : '0px');

    this.dispatch('collapse', { detail: { collapsed } });
  }
}
